import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from 'react';
import { Badge, Image, TableV2, Tooltip } from '@plerionhq/design-system';
import type { TableHeadingRenderProps } from '@plerionhq/design-system';
import type {
  ColumnDef,
  PaginationState,
  VisibilityState,
} from '@tanstack/react-table';
import './access-grants-table.css';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { AccessGrant } from '../../types';
import {
  isFederatedPrincipalType,
  mechanismDescriptions,
  mechanismLabels,
  originDescriptions,
  originDisplay,
  principalTypeLabels,
} from '../../label-maps';
import { ScopeBadge } from '../../components/scope-badge';
import { TrustBadge } from '../../components/trust-badge';
import { assetTypeLabel } from '../../lib/asset-type';
import {
  CommentCell,
  DecisionCell,
  NextReviewCell,
  GranteeCell,
} from './audit-cells';
import { GrantsViewToggle } from './grants-view-toggle';
import type { GrantsView } from './grants-views';
import type { TFunction } from 'i18next';

import { useTranslation } from 'react-i18next';

dayjs.extend(relativeTime);

function resourceName(grant: AccessGrant): string {
  return (
    grant.resourceLabel ?? grant.assetId.split(':').at(-1) ?? grant.assetId
  );
}

function principalName(grant: AccessGrant): string {
  return grant.principalLabel ?? grant.principal;
}

// Column order here is mirrored by the sidebar facets in access-grants-filters.tsx.
// Keep the two in sync when adding/removing columns.
const columns = (t: TFunction<'accessGrants'>): ColumnDef<AccessGrant>[] => [
  {
    id: 'resource',
    accessorFn: resourceName,
    header: t('assetName'),
    // Capped like Principal below: generated names run to 50+ characters and
    // otherwise set the table's width on their own.
    cell: ({ row }) => (
      <span
        className="block max-w-xs truncate font-medium text-gray-900 dark:text-gray-100"
        title={resourceName(row.original)}
      >
        {resourceName(row.original)}
      </span>
    ),
  },
  {
    id: 'principal',
    accessorFn: principalName,
    header: t('principal'),
    cell: ({ row }) => (
      <span className="block max-w-xs truncate" title={row.original.principal}>
        {principalName(row.original)}
      </span>
    ),
  },
  {
    id: 'mechanism',
    accessorKey: 'mechanism',
    header: t('mechanism'),
    cell: ({ row }) => (
      <Tooltip label={mechanismDescriptions(t)[row.original.mechanism]}>
        <span>{mechanismLabels(t)[row.original.mechanism]}</span>
      </Tooltip>
    ),
  },
  {
    id: 'grantScope',
    accessorKey: 'grantScope',
    header: t('scope'),
    cell: ({ row }) => <ScopeBadge scope={row.original.grantScope} />,
  },
  {
    id: 'grantOrigin',
    accessorKey: 'grantOrigin',
    header: t('origin'),
    cell: ({ row }) => {
      const { label, status } = originDisplay(t)[row.original.grantOrigin];
      return (
        <Tooltip label={originDescriptions(t)[row.original.grantOrigin]}>
          <Badge label={label} status={status} />
        </Tooltip>
      );
    },
  },
  {
    id: 'trustStatus',
    accessorKey: 'trustStatus',
    header: t('common:trust'),
    cell: ({ row }) => <TrustBadge grant={row.original} />,
  },
  {
    id: 'assetType',
    accessorKey: 'assetType',
    header: t('resourceType'),
    cell: ({ row }) => (
      <div className="flex items-center gap-2" title={row.original.assetType}>
        <Image
          type="resourceType"
          name={row.original.assetType}
          alt={row.original.assetType}
          className="h-5 w-5 flex-shrink-0 object-contain"
          fallback={{ type: 'provider', name: 'AWS', alt: 'AWS' }}
        />
        <span className="truncate">
          {assetTypeLabel(row.original.assetType)}
        </span>
      </div>
    ),
  },
  {
    id: 'principalType',
    accessorKey: 'principalType',
    header: t('principalType'),
    cell: ({ row }) =>
      principalTypeLabels(t)[row.original.principalType] ??
      row.original.principalType,
  },
  {
    id: 'resourceAccountId',
    accessorKey: 'resourceAccountId',
    header: t('assetAccount'),
    cell: ({ row }) => (
      <span className="font-mono text-xs">
        {row.original.resourceAccountId}
      </span>
    ),
  },
  {
    id: 'principalAccountId',
    accessorKey: 'principalAccountId',
    header: t('principalAccount'),
    cell: ({ row }) => {
      const isFederated = isFederatedPrincipalType(row.original.principalType);
      if (isFederated) {
        return <span className="text-gray-400">—</span>;
      }
      return row.original.principalAccountId ? (
        <span className="font-mono text-xs">
          {row.original.principalAccountId}
        </span>
      ) : (
        <span className="text-gray-400">—</span>
      );
    },
  },
  {
    id: 'resourceRegion',
    accessorKey: 'resourceRegion',
    header: t('assetRegion'),
    cell: ({ row }) => (
      <span className="font-mono text-xs">{row.original.resourceRegion}</span>
    ),
  },
  {
    id: 'service',
    accessorKey: 'service',
    header: t('service'),
  },
  {
    id: 'firstObservedAt',
    accessorKey: 'firstObservedAt',
    header: t('firstSeen'),
    cell: ({ row }) => (
      <span
        className="text-xs text-gray-500 dark:text-gray-400"
        title={new Date(row.original.firstObservedAt).toLocaleString()}
      >
        {dayjs(row.original.firstObservedAt).fromNow()}
      </span>
    ),
  },
  // Audit-lifecycle columns — editable inline, shown in Audit view. Sorting is
  // disabled: the grid only holds the paged-in rows, so a client-side sort on
  // these would be misleading. Column width is set by each editable cell's own
  // min-width (see audit-cells.tsx) — the non-virtualized TableV2 ignores the
  // column `minSize`, so the widths must live on the cell content.
  {
    id: 'grantee',
    accessorKey: 'grantee',
    header: t('grantee'),
    enableSorting: false,
    cell: ({ row }) => <GranteeCell grant={row.original} />,
  },
  {
    id: 'reviewDecision',
    accessorKey: 'reviewDecision',
    header: t('decision'),
    enableSorting: false,
    cell: ({ row }) => <DecisionCell grant={row.original} />,
  },
  {
    id: 'reviewComment',
    accessorKey: 'reviewComment',
    header: t('comment'),
    enableSorting: false,
    cell: ({ row }) => <CommentCell grant={row.original} />,
  },
  {
    id: 'nextReviewAt',
    accessorKey: 'nextReviewAt',
    header: t('nextReview'),
    enableSorting: false,
    cell: ({ row }) => <NextReviewCell grant={row.original} />,
  },
];

type GrantsHeadingValue = {
  activeView: GrantsView | null;
  onSelectView: (view: GrantsView) => void;
};

const GrantsHeadingContext = createContext<GrantsHeadingValue>({
  activeView: null,
  onSelectView: () => {},
});

// TableV2 renders `tableHeadingRender` as a component, so it has to keep one
// identity: an inline arrow is a new type every render, and React rebuilds the
// toolbar and closes the open popover. The view state comes through context for
// the same reason.
function GrantsTableHeading({
  columnVisibility,
}: TableHeadingRenderProps<AccessGrant>) {
  const { activeView, onSelectView } = useContext(GrantsHeadingContext);

  // `self-end` shrinks the row to its content at the right; `sticky right-0`
  // pins it to the visible right edge as the wide table scrolls.
  return (
    <div className="sticky right-0 z-10 flex items-center gap-3 self-end">
      <GrantsViewToggle view={activeView} onSelectView={onSelectView} />
      {/* The existing column-picker widget, unchanged. */}
      {columnVisibility}
    </div>
  );
}

// TableV2 always installs getPaginationRowModel and defaults to pageSize=10,
// regardless of `enablePagination`. enablePagination=false only hides the
// pagination UI — it does not stop TanStack Table from slicing rows. With
// data being driven by useInfiniteQuery, we want every accumulated row
// rendered so the sentinel sits at the true bottom. Passing a noop manual
// `pagination` prop sets manualPagination=true inside TableV2, which is the
// supported way to tell TanStack Table "don't slice; the consumer paginates
// externally". The page index / size / count values are inert under
// manualPagination=true but must satisfy the prop type.
const NO_INTERNAL_PAGINATION = {
  pageIndex: 0,
  pageSize: Number.MAX_SAFE_INTEGER,
  pageCount: 1,
  setPagination: () => {},
} satisfies PaginationState & {
  pageCount: number;
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>;
};

type AccessGrantsTableProperties = {
  grants: AccessGrant[];
  onSelect: (grant: AccessGrant) => void;
  isLoadingMore: boolean;
  hasNextPage: boolean;
  onLoadMore: () => void;
  // Scrollable ancestor that owns the overflow — the parent passes this so
  // the IntersectionObserver can use it as `root`. Defaulting to the
  // viewport doesn't work here: the page itself doesn't scroll, the parent
  // div does, and the sentinel never reaches the viewport edge.
  scrollRoot: RefObject<HTMLElement | null>;
  // Owned by the page, not this component: the table unmounts on every filter
  // change, which would reset a locally-held view. See use-grants-view.ts.
  columnVisibility: VisibilityState;
  setColumnVisibility: Dispatch<SetStateAction<VisibilityState>>;
  activeView: GrantsView | null;
  onSelectView: (view: GrantsView) => void;
};

export function AccessGrantsTable({
  grants,
  onSelect,
  isLoadingMore,
  hasNextPage,
  onLoadMore,
  scrollRoot,
  columnVisibility,
  setColumnVisibility,
  activeView,
  onSelectView,
}: AccessGrantsTableProperties) {
  const { t } = useTranslation('accessGrants');
  // A cell renderer is a component type to the table, so the column defs
  // must keep their identity or every cell remounts and loses its draft.
  const columnDefs = useMemo(() => columns(t), [t]);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const heading = useMemo(
    () => ({ activeView, onSelectView }),
    [activeView, onSelectView],
  );

  useEffect(() => {
    const el = sentinelRef.current;
    const rootEl = scrollRoot.current;
    if (!el || !rootEl || !hasNextPage) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isLoadingMore) {
          onLoadMore();
        }
      },
      { root: rootEl, threshold: 0.1 },
    );

    observer.observe(el);
    return () => observer.disconnect();
    // scrollRoot is a stable RefObject — useRef never changes object identity,
    // so it doesn't belong in the dep array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNextPage, isLoadingMore, onLoadMore]);

  // TableV2 gaps (track as design-system follow-ups, do not work around here):
  //  - onClickRow has no keyboard equivalent — rows are not focusable / Enter
  //    doesn't open the drawer. The old card list handled this; TableV2 should.
  //  - Column visibility (enableHiding + defaultHidden) is in-memory only —
  //    user toggles reset on every refresh. No localStorage persistence yet.
  return (
    // min-w-max lets this wrapper grow to the full table width when the table
    // is wider than the viewport, so the sticky toolbar's containing block
    // spans the whole horizontal scroll range and stays pinned (otherwise the
    // wrapper is only viewport-wide and the toolbar scrolls off with it). It
    // still collapses to viewport width when the table is narrow.
    <div className="grants-table-h-scroll flex min-w-max flex-col gap-4">
      <GrantsHeadingContext.Provider value={heading}>
        <TableV2<AccessGrant, Record<string, unknown>>
          data={grants}
          columns={columnDefs}
          defaultColumn={{ enableSorting: true }}
          visibility={{ columnVisibility, setColumnVisibility }}
          enableHiding
          enablePagination={false}
          enableRowClickable
          onClickRow={(row) => onSelect(row.original)}
          pagination={NO_INTERNAL_PAGINATION}
          tableHeadingRender={GrantsTableHeading}
          data-testid="grants-table"
        />
      </GrantsHeadingContext.Provider>
      <div ref={sentinelRef} className="py-2 text-center">
        {isLoadingMore && (
          <span className="text-sm text-gray-400 dark:text-gray-500">
            {t('loading')}
          </span>
        )}
      </div>
    </div>
  );
}
